/**
 * Where "Back to Organizations" should land.
 *
 * The list hands its own URL over as route state when a row is opened, so returning restores the
 * page and search the admin was looking at. A bare `/admin` is the fallback for an arrival that
 * carries no state: a pasted link, a refresh, or a redirect from somewhere else.
 *
 * Extracted from the detail view so it can be tested without mounting it. It is the half of the fix
 * that a test asserting only "the list passes `from`" would miss entirely — the list can pass a
 * perfect value to a component that ignores it.
 *
 * Route state is untrusted: it survives a refresh in the history entry and can be hand-edited, so a
 * non-string or an off-site value is discarded rather than navigated to.
 */
export const ADMIN_ORGANIZATIONS_PATH = '/admin';

/** A page number from the URL or storage is untrusted; anything unusable falls back to page 1. */
export const parsePage = (raw: string | null): number => {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
};

/**
 * The list view's page and search, encoded for the URL and for storage.
 *
 * Only non-default values are written, so the default view is a clean `/admin` rather than
 * `/admin?page=1&search=`.
 */
export const buildListParams = (
  pageNumber: number,
  searchTerm: string,
): URLSearchParams => {
  const params = new URLSearchParams();
  if (pageNumber > 1) params.set('page', String(pageNumber));
  if (searchTerm) params.set('search', searchTerm);
  return params;
};

export const resolveBackTarget = (state: unknown): string => {
  if (typeof state !== 'object' || state === null) {
    return ADMIN_ORGANIZATIONS_PATH;
  }

  const from = (state as { from?: unknown }).from;
  if (typeof from !== 'string' || from.length === 0) {
    return ADMIN_ORGANIZATIONS_PATH;
  }

  // Same-origin, app-relative paths only. `//host` is protocol-relative and would leave the app,
  // and anything not starting with `/admin` is not this list.
  if (!from.startsWith('/admin') || from.startsWith('//')) {
    return ADMIN_ORGANIZATIONS_PATH;
  }

  return from;
};


/**
 * sessionStorage key for the organizations list's page and search.
 *
 * Session-scoped, not local: "where I was in the list" should survive navigating in and out of
 * organizations and other sections, and be forgotten when the tab closes. Same reasoning and
 * lifetime as the CRM's `crmViewState` — deliberately mirrored rather than imported, so admin does
 * not reach into a CRM-scoped module.
 */
const LIST_STATE_KEY = 'admin:organizations:view';

/**
 * Remember the list's page and search for the rest of the session.
 *
 * An empty set of params clears the entry rather than storing a blank, so returning to a default
 * view later does not read back a stale page.
 */
export const saveAdminListState = (params: URLSearchParams): void => {
  try {
    const serialized = params.toString();
    if (!serialized) {
      sessionStorage.removeItem(LIST_STATE_KEY);
      return;
    }
    sessionStorage.setItem(LIST_STATE_KEY, serialized);
  } catch (error) {
    console.warn('Failed to save admin list state to sessionStorage:', error);
  }
};

/** The remembered page and search, or null when there is nothing usable stored. */
export const loadAdminListState = (): URLSearchParams | null => {
  try {
    const stored = sessionStorage.getItem(LIST_STATE_KEY);
    if (!stored) return null;
    return new URLSearchParams(stored);
  } catch (error) {
    console.warn('Failed to load admin list state from sessionStorage:', error);
    return null;
  }
};

/**
 * Where the list should open: the URL when it names anything, otherwise the remembered session
 * state, otherwise the default view.
 *
 * URL wins so a pasted or shared link always shows what it says, rather than being overridden by
 * wherever this tab happened to be.
 */
export const resolveInitialListState = (
  searchParams: URLSearchParams,
): { pageNumber: number; searchTerm: string } => {
  const source =
    searchParams.has('page') || searchParams.has('search')
      ? searchParams
      : (loadAdminListState() ?? new URLSearchParams());

  return {
    pageNumber: parsePage(source.get('page')),
    searchTerm: source.get('search') ?? '',
  };
};
